import { FormField, FormError } from './utils';
import { accountForUTC, checkDate, padMonth, includesDate } from '../utilities/date';

export function validateDate(f: FormField, validationType: string): FormError {
  let formError = { type: validationType, message: 'This is not a valid date.' };

  if ((f.field as HTMLInputElement).valueAsDate) {
    return checkDate((f.field as HTMLInputElement).valueAsDate) ? null : formError;
  } else {
    return checkDate(f.field.value) ? null : formError;
  }
}

export function validateDateWithinRange(f: FormField, validationType: string) {
  let value = new Date(f.field.value);

  let formError = {
    type: validationType,
    message: f.message || 'The date provided is not within the range required.',
  };

  if (value instanceof Date) {
    return f.options.minDate <= value && value <= f.options.maxDate ? null : formError;
  } else {
    return formError;
  }
}

export function validateDateNotDisallowedDate(f: FormField, validationType: string): FormError {
  let value = new Date(f.field.value);

  let formError = {
    type: validationType,
    message: f.message || 'The date provided is disallowed.',
  };

  if (value instanceof Date) {
    return !includesDate(f.options.disallowedDatesParsed, value) ? null : formError;
  } else {
    return formError;
  }
}

export function validateMinimumAge(f: FormField, validationType: string): FormError {
  let value = new Date(f.field.value);
  let age = Number.parseInt(f.field.dataset.validateMinAge.valueOf());
  let date = new Date();
  let birthYear = date.getFullYear() - age;

  let formError = {
    type: validationType,
    message: f.message || `Age of applicant must be older than ${age}.`,
  };

  date.setFullYear(birthYear);

  return value > date ? formError : null;
}

export function validateMaximumAge(f: FormField, validationType: string): FormError {
  let value = new Date(f.field.value);
  let age = Number.parseInt(f.field.dataset.validateMaxAge.valueOf());
  let date = new Date();
  let birthYear = date.getFullYear() - age;

  let formError = {
    type: validationType,
    message: f.message || `Age of applicant must be younger than ${age}.`,
  };

  date.setFullYear(birthYear);

  return value < date ? formError : null;
}

export function validateMinDate(f: FormField, validationType: string): FormError {
  let value = new Date(f.field.value);
  let minDate = f.options?.minDate || new Date();

  let formError = {
    type: validationType,
    message: f.message || `The date entered is earlier than what is accepted.`,
  };

  return accountForUTC(value) < accountForUTC(minDate) ? formError : null;
}

export function validateMaxDate(f: FormField, validationType: string): FormError {
  let value = new Date(f.field.value);
  let maxDate = f.options?.maxDate || new Date();

  let formError = {
    type: validationType,
    message: f.message || `The date entered is later than what is accepted.`,
  };

  return accountForUTC(value) > accountForUTC(maxDate) ? formError : null;
}

export function validateMinimumYear(f: FormField, validationType: string): FormError {
  let value = f.field.value;
  let minYear = f.field.dataset.validateMinYear;

  let formError = {
    type: validationType,
    message: f.message || `The year supplied is earlier than what is accepted.`,
  };

  return value < minYear ? formError : null;
}

export function validateMaximumYear(f: FormField, validationType: string): FormError {
  let value = f.field.value;
  let maxYear = f.field.dataset.validateMaxYear;

  let formError = {
    type: validationType,
    message: f.message || `The year supplied is later than what is accepted.`,
  };

  return value > maxYear ? formError : null;
}

export function validateMinMonthYear(f: FormField, validationType: string): FormError {
  let value = f.field.value.split('/');
  let valueAsDate = accountForUTC(new Date());
  let minDate = f.options.minDate ? f.options.minDate : accountForUTC(new Date());
  let formError = {
    type: validationType,
    message: f.message || 'The month and year supplied are not valid.',
  };

  if (parseInt(value[0]) === 0) {
    return formError;
  }

  let validInput =
    value[0].length === 2 &&
    parseInt(value[0]) < 13 &&
    value[1].length === 4 &&
    parseInt(value[0]) < minDate.getFullYear() + 30;

  if (validInput) {
    valueAsDate.setDate(1);
    valueAsDate.setFullYear(parseInt(value[1]));
    valueAsDate.setMonth(parseInt(value[0]) - 1);

    if (valueAsDate.getFullYear() > minDate.getFullYear()) {
      return null;
    } else if (valueAsDate.getFullYear() === minDate.getFullYear()) {
      if (valueAsDate.getMonth() >= minDate.getMonth()) {
        return null;
      } else {
        return formError;
      }
    }

    return formError;
  } else {
    return formError;
  }
}

export function validateMinMonthYearSet(
  month: string,
  year: string,
  validationType: string,
  message?: string
): FormError {
  const monthPadded = padMonth(month);
  let valueAsDate = accountForUTC(new Date());
  let minDate = accountForUTC(new Date());
  let formError = {
    type: validationType,
    message: message || 'The month and year supplied are not valid.',
  };

  if (parseInt(monthPadded) === 0) {
    return formError;
  }

  let validInput =
    monthPadded.length === 2 &&
    parseInt(monthPadded) < 13 &&
    year.length === 4 &&
    parseInt(year) < minDate.getFullYear() + 30;

  if (validInput) {
    valueAsDate.setDate(1);
    valueAsDate.setFullYear(parseInt(year));
    valueAsDate.setMonth(parseInt(monthPadded) - 1);

    if (valueAsDate.getFullYear() > minDate.getFullYear()) {
      return null;
    } else if (valueAsDate.getFullYear() === minDate.getFullYear()) {
      if (valueAsDate.getMonth() >= minDate.getMonth()) {
        return null;
      } else {
        return formError;
      }
    }

    return formError;
  } else {
    return formError;
  }
}
